import { datadogLogs } from '@datadog/browser-logs';

/**
 * Thin wrapper over the (already-initialized) Datadog Logs SDK for structured FE logs.
 * `env`/`service`/`version` + RUM session are attached automatically by `datadogLogs.init`
 * (see `index.tsx`), so callers only pass domain context.
 *
 * No-op in local `vite dev` so a developer's machine doesn't forward logs to Datadog (which would
 * tag them under the default `integration` env). Every deployed env logs under its own `env` tag.
 */
type LogContext = Record<string, unknown>;

const enabled = !import.meta.env.DEV;

// Only forward a real Error (message + stack); coerce anything else into an Error so we never leak a
// raw payload. Avoid String(obj) (→ "[object Object]"): JSON-stringify non-strings, safely.
const toError = (error: unknown): Error | undefined => {
  if (error === null || error === undefined) {
    return undefined;
  }
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error('Non-serializable error');
  }
};

export const logger = {
  info: (message: string, context?: LogContext) => {
    if (enabled) {
      datadogLogs.logger.info(message, context);
    }
  },
  warn: (message: string, context?: LogContext, error?: unknown) => {
    if (enabled) {
      datadogLogs.logger.warn(message, context, toError(error));
    }
  },
  error: (message: string, context?: LogContext, error?: unknown) => {
    if (enabled) {
      datadogLogs.logger.error(message, context, toError(error));
    }
  },
};
